import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Link } from 'react-router-dom'
import { ReactComponent as Briefcase } from '../../assets/svgs/briefcase (1).svg'
import { ReactComponent as Star } from '../../assets/svgs/star.svg'
import { ReactComponent as Gear } from '../../assets/svgs/gearFigma.svg'
import { ReactComponent as PersonLines } from '../../assets/svgs/personUsers.svg'
import { ReactComponent as HouseDoor } from '../../assets/svgs/houseDoor.svg'
import { ReactComponent as ClipboardCheck } from '../../assets/svgs/clipboardCheck.svg'
import { ReactComponent as Building } from '../../assets/svgs/building.svg'
import { ReactComponent as Chart } from '../../assets/svgs/barChart.svg'
import { ReactComponent as Database } from '../../assets/svgs/database.svg'
import { ReactComponent as UICheck } from '../../assets/svgs/ui-checks.svg'
import { ReactComponent as ChevronDown } from '../../assets/svgs/chevronDown.svg'
import { ReactComponent as Calendar } from '../../assets/svgs/calendar.svg'
import { ReactComponent as OrgChart } from '../../assets/svgs/disc.svg'
import { ReactComponent as Hierarchy } from '../../assets/svgs/hierarchy-square.svg'
import { ReactComponent as Chat } from '../../assets/svgs/chat.svg'
import { ReactComponent as Pocket } from '../../assets/svgs/pocket.svg'
import { ReactComponent as BoardCheck } from '../../assets/svgs/boardCheck.svg'
import { baseLayout } from '../../constants/theme'

export const SidebarMenuIcon = styled('span')(() => ({
  marginRight: `calc(${baseLayout.menuItemSpacing} / 2 + 0.7px)`,
}))

export const HouseIcon = SidebarMenuIcon.withComponent(HouseDoor)
export const TriangleIcon = SidebarMenuIcon.withComponent(Briefcase)
export const StarIcon = SidebarMenuIcon.withComponent(Star)
export const PersonIcon = SidebarMenuIcon.withComponent(PersonLines)
export const FileIcon = SidebarMenuIcon.withComponent(ClipboardCheck)
export const GearIcon = SidebarMenuIcon.withComponent(Gear)
export const BuildingIcon = SidebarMenuIcon.withComponent(Building)
export const ChartIcon = SidebarMenuIcon.withComponent(Chart)
export const DatabaseIcon = SidebarMenuIcon.withComponent(Database)
export const FormIcon = SidebarMenuIcon.withComponent(UICheck)
export const CalendarIcon = SidebarMenuIcon.withComponent(Calendar)
export const OrgChartIcon = SidebarMenuIcon.withComponent(OrgChart)
export const HierarchyIcon = SidebarMenuIcon.withComponent(Hierarchy)
export const ChatIcon = SidebarMenuIcon.withComponent(Chat)
export const OfferIcon = SidebarMenuIcon.withComponent(Pocket)
export const AssessmentIcon = SidebarMenuIcon.withComponent(BoardCheck)

export const VerticalMenu = styled('div')(({ collapsed, theme }) => ({
  width: collapsed
    ? `${baseLayout.sidebarCollapsedWidth} !important`
    : baseLayout.sidebarWidth,
  zIndex: '1001',
  background: theme.ateneoBlue,
  bottom: '0',
  marginTop: '0',
  position: 'fixed',
  top: baseLayout.headerHeight,
  paddingTop: 0,
  boxShadow: theme.boxShadow,
  transition: 'width 0.3s ease-in-out',
  '@media (max-width: 992px)': {
    width: `${baseLayout.sidebarCollapsedWidth} !important`,
    transition: 'width 0.3s ease-in-out',
  },
  '@media print': {
    display: 'none !important',
  },
}))

export const MenuItemBadge = styled('span')(
  ({
    backgroundColor = '#C73641',
    borderColor,
    width = '20px',
    fontSize = '11px',
    top,
    right,
    theme,
  }) => ({
    color: theme.white,
    minWidth: width,
    height: width,
    padding: '0 4px',
    fontSize,
    backgroundColor,
    borderRadius: 'calc(infinity * 1px)',
    fontWeight: '500',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',

    ...(top &&
      right && {
        position: 'absolute',
        top,
        right,
        transform: 'translateY(-50%)',
      }),
  }),
)

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`

export const StyledSidebar = styled.aside({
  color: 'white',
  position: 'relative',
  width: '100%',
  alignItems: 'center',
})

export const StyledTopSpacing = styled.div({
  height: '16px',
})

export const SectionTitle = styled.div(({ theme }) => ({
  padding: '0 12px',
  fontSize: '10.5px',
  color: theme.babyBlue,
  marginBottom: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: 500,
  width: '100%',
}))

export const MenuButton = styled.button(({ active, isExpanded, theme }) => ({
  display: 'flex',
  background: active ? theme.sidebar : 'transparent',
  border: 'none',
  alignItems: 'center',
  padding: isExpanded ? '6px 12px' : '6px 0 6px 14px',
  margin: '0 auto',
  borderRadius: '6px',
  transition: 'background-color 200ms',
  fontSize: '13px',
  fontWeight: 600,
  color: 'white',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: theme.sidebar,
  },
  ...(isExpanded
    ? {
        justifyContent: 'space-between',
        width: 'calc(100% - 12px)',
      }
    : {
        justifyContent: 'center',
        width: '44px',
      }),
  ':dir(rtl)': {
    padding: isExpanded ? '6px 12px' : '6px 14px 6px 0',
  },
}))

export const IconContainer = styled.div(({ isExpanded, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  justifyContent: 'center',
  position: 'relative',
  '& span': {
    opacity: isExpanded ? 1 : 0,
    maxWidth: isExpanded ? '100%' : 0,
    transition: 'opacity 0.2s ease 0.1s, max-width 0.3s ease-in-out',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}))

export const DropdownIcon = styled(ChevronDown, {
  shouldForwardProp: (prop) => prop !== '$isOpen',
})(({ $isOpen }) => ({
  transition: 'transform 200ms',
  transform: `rotate(${$isOpen ? '180deg' : '0'})`,
}))

export const Tooltip = styled.div(({ dir, theme }) => ({
  position: 'fixed',
  backgroundColor: theme.ateneoBlue,
  color: 'white',
  borderRadius: '6px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  minWidth: '160px',
  padding: '4px 0',
  animation: `${fadeIn} 200ms ease-out`,
  border: `1px solid ${theme.sidebar}`,
  zIndex: 1002,
  ...(dir === 'rtl' && {
    right: 'auto',
    left: 'auto',
    transformOrigin: 'right',
    '&:before': {
      right: '-8px',
      left: 'auto',
      borderRightColor: theme.sidebar,
    },
  }),
}))

export const NestedTooltip = styled.div(({ theme }) => ({
  position: 'absolute',
  left: '100%',
  top: 0,
  marginLeft: '4px',
  backgroundColor: theme.ateneoBlue,
  borderRadius: '6px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  minWidth: '160px',
  padding: '4px 0',
  transformOrigin: 'left',
  animation: `${fadeIn} 200ms ease-out`,
  border: `1px solid ${theme.sidebar}`,
  zIndex: 1002,

  ':dir(rtl)': {
    left: 'auto',
    right: '100%',
    marginLeft: 0,
    marginRight: '4px',
    transformOrigin: 'right',
  },
}))

export const TooltipItem = styled.div(({ theme }) => ({
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 600,
  borderBottom: `1px solid ${theme.sidebar}`,
  ':dir(rtl)': {
    textAlign: 'right',
  },
  width: '100%',
}))

export const SubItemLink = styled(Link)(({ active, theme }) => ({
  display: 'flex',
  width: '100%',
  padding: '6px 12px',
  fontSize: '12.5px',
  fontWeight: 600,
  transition: 'background-color 200ms',
  textDecoration: 'none',
  color: 'inherit',
  backgroundColor: active ? theme.sidebar : 'transparent',
  '&:hover': {
    backgroundColor: theme.sidebar,
  },
  ':dir(rtl)': {
    textAlign: 'right',
  },
}))

export const DropdownMenu = styled.div(({ theme }) => ({
  backgroundColor: theme.sidebar,
  margin: '0 auto',
  borderRadius: '6px',
  marginTop: '2px',
  width: 'calc(100% - 12px)',
}))

export const SectionContainer = styled.div({
  width: '100%',
  padding: '16px 0',
  display: 'flex',
  flexDirection: 'column',
})

export const SubItemsContainer = styled.div({
  padding: '4px 0',
  width: '100%',
  ':dir(rtl) &': {
    direction: 'rtl',
    textAlign: 'right',
  },
})

export const MenuItemWrapper = styled.div({
  position: 'relative',
})

export const DropdownSubItemLink = styled(SubItemLink)(({ theme }) => ({
  padding: '6px 12px',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: theme.sidebarHover,
  },
  ':dir(rtl)': {
    textAlign: 'right',
    paddingRight: '12px',
  },
}))

export const ScrollList = styled('ul')({
  display: 'grid',
  gridAutoFlow: 'row',
  overflowY: 'auto',
  overscrollBehaviorInline: 'contain',
  padding: '0 !important',
  maxHeight: '80vh',
})
